import * as React from "react";
import { useLocation } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useBreakpoint } from "@/utils/hooks/useBreakpoint";
import NavBar from "@/components/navbars/Navbar";
import { AppShellContext } from "@/components/layouts/app-shell-context";

const SIDEBAR_EXPANDED_WIDTH = 280;
const SIDEBAR_COLLAPSED_WIDTH = 72;
const SIDEBAR_COLLAPSED_KEY = "gs-sidebar-collapsed";

type AppShellProps = {
    sidebar: React.ReactNode;
    children: React.ReactNode;
    className?: string;
};

export function AppShell({ sidebar, children, className }: AppShellProps) {
    const { isMobile, isTablet, isDesktop, sidebarMode } = useBreakpoint();
    const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
    const location = useLocation();

    React.useEffect(() => {
        setMobileSidebarOpen(false);
    }, [location.pathname]);

    React.useEffect(() => {
        if (isMobile) {
            setMobileSidebarOpen(false);
        }
    }, [isMobile]);

    React.useEffect(() => {
        if (isTablet) {
            const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
            setSidebarCollapsed(stored === null ? true : stored === "true");
        } else if (isDesktop) {
            setSidebarCollapsed(false);
        }
    }, [isTablet, isDesktop]);

    const toggleSidebarCollapsed = React.useCallback(() => {
        if (!isTablet) return;
        setSidebarCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
            return next;
        });
    }, [isTablet]);

    const showDesktopSidebar = !isMobile;
    const isSidebarCollapsed = isTablet && sidebarCollapsed;
    const sidebarWidth =
        showDesktopSidebar && isSidebarCollapsed
            ? SIDEBAR_COLLAPSED_WIDTH
            : showDesktopSidebar
              ? SIDEBAR_EXPANDED_WIDTH
              : 0;

    const shellStyle = {
        "--gs-sidebar-width": `${sidebarWidth}px`,
    } as React.CSSProperties;

    return (
        <AppShellContext.Provider
            value={{
                mobileSidebarOpen,
                setMobileSidebarOpen,
                sidebarCollapsed: isSidebarCollapsed,
                setSidebarCollapsed,
                toggleSidebarCollapsed,
                sidebarMode,
                isMobile,
                isTablet,
                isDesktop,
            }}
        >
            <div
                className={cn(
                    "gs-app-shell",
                    isMobile && "gs-app-shell--mobile",
                    isTablet && "gs-app-shell--tablet",
                    isDesktop && "gs-app-shell--desktop",
                    isSidebarCollapsed && "gs-app-shell--sidebar-collapsed",
                    className
                )}
                style={shellStyle}
            >
                <NavBar />

                {isMobile && (
                    <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
                        <SheetContent
                            side="left"
                            showCloseButton={false}
                            overlayClassName="gs-nav-sheet-overlay"
                            className="gs-nav-sheet gs-app-sidebar-sheet w-[280px] max-w-[85vw]"
                        >
                            <div className="gs-nav-sheet-inner">{sidebar}</div>
                        </SheetContent>
                    </Sheet>
                )}

                {showDesktopSidebar && (
                    <div
                        className={cn(
                            "gs-app-sidebar-host",
                            isSidebarCollapsed && "gs-app-sidebar-host--collapsed"
                        )}
                    >
                        {sidebar}
                    </div>
                )}

                <main
                    className={cn(
                        "gs-app-main",
                        showDesktopSidebar && "gs-app-main--with-sidebar"
                    )}
                >
                    <div className="gs-page-content">{children}</div>
                </main>
            </div>
        </AppShellContext.Provider>
    );
}

export default AppShell;
